import { isAction, isAnyOf, type Middleware } from "@reduxjs/toolkit";

import {
  clearDictationAudio,
  resetDictationState,
} from "../../features/dictation/store/dictationSlice";
import { clearDictationDraft } from "../../features/dictation/store/draftStorage";
import { resetStudents } from "../../features/students/store/studentsSlice";
import { resetMaterials } from "../../features/texts/store/materialsSlice";
import { resetTextsState } from "../../features/texts/store/textsSlice";
import { clearCache } from "../../utils/cache/cache";
import { resetAdaptiveStudyState } from "../slices/adaptiveStudySlice";
import {
  loginUser,
  register,
  logout,
  getCurrentUser,
  setAuth,
} from "../slices/authSlice";
import { resetDictionaryState } from "../slices/dictionarySlice";
import { resetLearning } from "../slices/learningSlice";
import { resetPaymentsState } from "../slices/paymentsSlice";
import { resetPracticeState } from "../slices/practiceSlice";
import {
  resetProfileState,
  resetProgressStats,
  initializeProfile,
  syncProfile,
  toProfileData,
  updateProfile,
} from "../slices/profileSlice";
import { resetSubscriptionState } from "../slices/subscriptionSlice";

import type { RootState } from "../index";

/** Isolate async results by session and synchronize profile after authentication. */
export const authStateCleanupMiddleware: Middleware = (api) => {
  const requests = new Map<string, number>();
  const learningRequests = new Map<string, number>();
  let learningVersion = 0;
  let sessionVersion = 0;
  return (next) =>
    (action): unknown => {
      const before = (api.getState() as RootState).auth;
      if (
        isAction(action) &&
        "meta" in action &&
        action.meta &&
        typeof action.meta === "object"
      ) {
        const meta = action.meta;
        if (
          "requestId" in meta &&
          typeof meta.requestId === "string" &&
          "requestStatus" in meta
        ) {
          if (meta.requestStatus === "pending") {
            requests.set(meta.requestId, sessionVersion);
            if (
              action.type.startsWith("dictionary/") ||
              action.type.startsWith("adaptive/") ||
              action.type.startsWith("learning/") ||
              action.type.startsWith("texts/") ||
              action.type.startsWith("practice/") ||
              action.type.startsWith("dictation/") ||
              action.type.startsWith("profile/fetchProgressStats/") ||
              action.type.startsWith("profile/fetchUserProfile/") ||
              action.type.startsWith("auth/getCurrentUser/")
            )
              learningRequests.set(meta.requestId, learningVersion);
          } else if (requests.has(meta.requestId)) {
            const version = requests.get(meta.requestId);
            const learningRequestVersion = learningRequests.get(meta.requestId);
            requests.delete(meta.requestId);
            learningRequests.delete(meta.requestId);
            if (
              version !== sessionVersion ||
              (learningRequestVersion !== undefined &&
                learningRequestVersion !== learningVersion)
            )
              return action;
          }
        }
      }
      const result = next(action);
      const after = (api.getState() as RootState).auth;
      const identityChanged = before.user?.id !== after.user?.id;
      const sessionChanged = before.token !== after.token;
      if (
        !identityChanged &&
        before.user &&
        after.user &&
        (before.user.targetLanguage !== after.user.targetLanguage ||
          before.user.nativeLanguage !== after.user.nativeLanguage ||
          before.user.level !== after.user.level)
      ) {
        learningVersion += 1;
        clearCache();
        clearDictationDraft();
        clearDictationAudio();
        [
          "dashboardCachedTexts",
          "dashboardCachedPagination",
          "dashboardFilter",
          "dashboardCachedUserId",
          "dashboardCachedTargetLanguage",
        ].forEach((key): void => localStorage.removeItem(key));
        api.dispatch(resetLearning());
        api.dispatch(resetDictationState());
        api.dispatch(resetTextsState());
        api.dispatch(resetPracticeState());
        api.dispatch(resetProgressStats());
        api.dispatch(resetDictionaryState());
        api.dispatch(resetAdaptiveStudyState());
      }
      if (
        (before.user !== null && identityChanged) ||
        sessionChanged ||
        logout.match(action)
      ) {
        sessionVersion += 1;
        clearCache();
        clearDictationAudio();
        api.dispatch(resetLearning());
        api.dispatch(resetDictationState());
        api.dispatch(resetTextsState());
        api.dispatch(resetMaterials());
        api.dispatch(resetStudents());
        api.dispatch(resetPracticeState());
        api.dispatch(resetDictionaryState());
        api.dispatch(resetAdaptiveStudyState());
        api.dispatch(resetSubscriptionState());
        api.dispatch(resetPaymentsState());
        api.dispatch(resetProfileState());
      }
      if (
        (identityChanged ||
          isAnyOf(
            loginUser.fulfilled,
            register.fulfilled,
            getCurrentUser.fulfilled,
            setAuth,
          )(action)) &&
        after.user
      ) {
        api.dispatch(syncProfile(toProfileData(after.user)));
      }
      if (
        updateProfile.fulfilled.match(action) &&
        after.user &&
        after.token &&
        action.payload.id === after.user.id
      ) {
        api.dispatch(
          setAuth({
            user: { ...after.user, ...action.payload },
            token: after.token,
          }),
        );
        api.dispatch(initializeProfile(toProfileData(action.payload)));
      }
      return result;
    };
};
